/**
 * DASHBOARD-QUESTIONS.WIDGET
 * Display list of results for a question
 */

import { connect } from "react-redux"
import Card from "@/components/card"
import { useEffect, useMemo, useState } from "react"
import { FilterState } from "@/redux/filter.types"
import { store } from "@/index"
import { loadParticipation, loadQuestionsAndTopics } from "@/redux/_archive.actions"
import { TopicState } from "@/redux/topic.types"
import { Axis, AxisState } from "@/redux/axis.types"
import { topicOpen, topicStatus } from "@/redux/topic.actions"
import { SurveyState } from "@/redux/survey.types"
import PageLoader from "@/components/page-loader"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING } from "@/redux/_status.types"
import NoResultsWidget from "./no-results.widget"
import { Session } from "@/redux/_session.types"
import LoadingModal from "@/modals/loading.modal"
import DashboardAxisWidget from "./dashboard-axis.widget"
import DashboardQuestionsSkeletonWidget from "./skeletons/dashboard-questions-skeleton.widget"
import { Question, QuestionState } from "@/redux/question.types"
import { questionActivate } from "@/redux/question.actions"
import QuestionTipsWidget from "./question-tips.widget"
import HelpModal from "@/components/modal-help"
import { benchmarkFetchQuestions } from "@/redux/benchmark.actions"
import { BenchmarkContext } from "@/features/benchmark/entities/question/model/benchmarkQuestion.context"
import type { BenchmarkEntry } from "@/features/benchmark/entities/question/model/benchmarkQuestion.context"
import { buildBenchmarkByUuid, benchmarkDelta } from "@/features/benchmark/entities/question/model/benchmarkQuestion.utils"
import BenchmarkDetailModal from "@/features/benchmark/entities/question/ui/BenchmarkDetailModal"
import { useAppSelector } from "@/core/root-reducer"
import { selectCanSeeBenchmark } from "@/features/benchmark/entities/question/model/benchmarkQuestion.selector"
import { motion } from "framer-motion"
import { useRowReveal } from "@/utils/row-reveal.utils"

interface StateProps {
  _session: Session
  axis: AxisState
  filter: FilterState
  question: QuestionState
  survey: SurveyState
  topic: TopicState
}

function DashboardQuestionsWidget(props: StateProps) {
  //Les cartes d'axes arrivent en cascade (un pas plus large que pour des lignes)
  const rowReveal = useRowReveal(false, 0.08)
  const canSeeBenchmark = useAppSelector(selectCanSeeBenchmark)

  const [benchmarkVisible, setBenchmarkVisible] = useState(
    () => canSeeBenchmark && localStorage.getItem("benchmark_visible") !== "false"
  )

  useEffect(() => {
    function handleToggle() {
      setBenchmarkVisible(localStorage.getItem("benchmark_visible") !== "false")
    }
    window.addEventListener("benchmark-visible-toggle", handleToggle)
    return () => window.removeEventListener("benchmark-visible-toggle", handleToggle)
  }, [])

  const [benchmarkDetailOpen, setBenchmarkDetailOpen] = useState(false)
  const [benchmarksByAid, setBenchmarksByAid] = useState<Record<string, BenchmarkEntry>>({})
  const [benchmarkLoading, setBenchmarkLoading] = useState(true)
  const [benchmarkScore, setBenchmarkScore] = useState<number>(0)
  const [benchmarkAxisColor, setBenchmarkAxisColor] = useState<string>("")
  const [benchmarkQuestionAid, setBenchmarkQuestionAid] = useState<string | null>(null)
  const [benchmarkEntry, setBenchmarkEntry] = useState<BenchmarkEntry | null>(null)
  type NavQuestion = {
    label: string
    axisLabel: string
    liveQuestionId: string
    topicLabel: string
    questionAid: string
    score: number
    axisColor: string
    delta: number
    entry: BenchmarkEntry
  }

  const navQuestions = useMemo<NavQuestion[]>(() => {
    const results: NavQuestion[] = []
    props.topic.list.forEach((topic) => {
      const axis = props.axis.list.find((a) => a.id === topic.AxisId)
      topic.Questions.forEach((q) => {
        if (q.type !== "scale" || !q.note || q.note <= 0) return
        const entry = benchmarksByAid[q.id]
        if (!entry) return
        results.push({
          label: q.label,
          topicLabel: topic.label,
          axisLabel: axis?.label || "",
          liveQuestionId: q.id,
          questionAid: entry.canonicalAid ?? q.id,
          score: q.note,
          axisColor: axis?.color || "#9CA3AF",
          delta: benchmarkDelta(q.note, entry.value),
          entry
        })
      })
    })
    return results
  }, [props.topic.list, props.axis.list, benchmarksByAid])

  const currentNavIndex = navQuestions.findIndex((q) => q.questionAid === benchmarkQuestionAid)

  function navigateTo(index: number) {
    if (index < 0 || index >= navQuestions.length) return
    const q = navQuestions[index]
    openDetailPanel(q.score, q.axisColor, q.questionAid, q.entry)
  }

  function openDetailPanel(
    score: number,
    axisColor: string,
    questionAid: string | null,
    entry: BenchmarkEntry | null
  ) {
    setBenchmarkScore(score)
    setBenchmarkAxisColor(axisColor)
    setBenchmarkQuestionAid(questionAid)
    setBenchmarkEntry(entry)
    setBenchmarkDetailOpen(true)
  }

  function closeDetailPanel() {
    setBenchmarkDetailOpen(false)
  }


  useEffect(() => {
    async function fetchSector() {
      if (!canSeeBenchmark) {
        setBenchmarkLoading(false)
        return
      }
      const r: any = await (store.dispatch(
        benchmarkFetchQuestions()
      ) as any)
      if (!r.error) {
        setBenchmarksByAid(buildBenchmarkByUuid(r))
      }
      setBenchmarkLoading(false)
    }
    fetchSector()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.survey.active.id])

  //Load questions on every change on the dashboard filters
  useEffect(() => {
    store.dispatch(loadQuestionsAndTopics(props.filter.dashboard))
    store.dispatch(loadParticipation(props.filter.dashboard))
  }, [props.filter.dashboard, props.survey.active.randomData])

  //Detect when page is load if scroll down to specific topic is required
  useEffect(() => {
    if (props.topic.status === "PAGE_LOADED") {
      const queryParams = new URLSearchParams(window.location.search)
      const topicId: string = queryParams.get("topic_id")!
      const axisId: string = queryParams.get("axis_id")!

      if (topicId) {
        const topic = props.topic.list.find((x) => x.id === topicId)
        if (topic) {
          if (!topic.open) store.dispatch(topicOpen(topic))
          document.querySelector("#details-topic-" + topicId)?.scrollIntoView({
            behavior: "smooth"
          })
        }
      } else if (axisId) {
        document.querySelector("#details-axis-" + axisId)?.scrollIntoView({
          behavior: "smooth"
        })
      }

      //Reset URL params and mark page as loaded
      window.history.replaceState(window.history.state, "", window.location.pathname)
      store.dispatch(topicStatus(STATUS_LOADED))
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.topic.status])

  //reset active question on load
  useEffect(() => {
    store.dispatch(questionActivate(new Question()))
  }, [])

  return props.topic.status === INSUFFICIENT_PARTICIPATION ? (
    <NoResultsWidget />
  ) : (
    <PageLoader status={props.topic.status} forceLoading skeleton={<DashboardQuestionsSkeletonWidget />}>
      {props.axis.status === STATUS_LOADING && <LoadingModal />}

      {
        /*Owl assistant giving advices thanks to gpt*/
        props.question.active.id.length > 0 && (
          <HelpModal
            title={`Conseils sur la question « ${props.question.active.label} »`}
            onClose={() => store.dispatch(questionActivate(new Question()))}
            isLeft>
            <QuestionTipsWidget question={props.question.active} />
          </HelpModal>
        )
      }

      <BenchmarkContext.Provider
        value={{
          benchmarksByAid,
          benchmarkLoading,
          benchmarkVisible,
          openDetail: openDetailPanel
        }}>
        {props.axis.list.concat([new Axis()]).map((axis: Axis, i: number) =>
          props._session.userRole === "OBSERVER" && axis.id === "" ? null : (
            <motion.div key={i} {...rowReveal(i)}>
              <Card isWithoutPadding helpSection="questions">
                <DashboardAxisWidget axis={axis} isAxisOpenable />
              </Card>
            </motion.div>
          )
        )}
      </BenchmarkContext.Provider>

      {benchmarkDetailOpen && (
        <BenchmarkDetailModal
          accountName={props._session.accountName}
          accountOptions={props._session.dashboardDisplaySettings}
          score={benchmarkScore}
          axisColor={benchmarkAxisColor}
          questionAid={benchmarkQuestionAid}
          benchmarkEntry={benchmarkEntry}
          // Not the account's own sector — this question may have fallen back
          // to the all/all row (see buildBenchmarkByUuid), so we scope the histogram/label to
          // whichever sector was actually used for THIS entry, not the account's sector.
          sector={benchmarkEntry?.sector ?? null}
          navItems={navQuestions}
          navCurrentIndex={currentNavIndex}
          onNavigate={navigateTo}
          onClose={closeDetailPanel}
        />
      )}
    </PageLoader>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  axis: state.axis,
  filter: state.filter,
  question: state.question,
  survey: state.survey,
  topic: state.topic
})

export default connect(mapStateToProps)(DashboardQuestionsWidget)
